/* eslint-disable no-underscore-dangle */

class BrowserStorage {
  private readonly _storage: Storage;

  private readonly _nameSpacePrefix: string;

  private readonly _isSupported: boolean;

  private prefixedKey(key: string): string {
    return `${this._nameSpacePrefix}.${key}`;
  }

  private testSupport() {
    try {
      const testKey = this.prefixedKey('test');
      this._storage.setItem(testKey, '1');
      this._storage.removeItem(testKey);
      return !this._storage.getItem(testKey); // no longer in store
    } catch {
      return false;
    }
  }

  public constructor(storage: Storage, nameSpacePrefix: string) {
    this._storage = storage;
    this._nameSpacePrefix = nameSpacePrefix;
    this._isSupported = this.testSupport();
  }

  public unPrefixKey(key: string | null) {
    return key ? key.replace(`${this._nameSpacePrefix}.`, '') : key;
  }

  public saveItem<T>(key: string, value: T) {
    if (this._isSupported) {
      try {
        this._storage.setItem(this.prefixedKey(key), JSON.stringify(value));
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }

  public getItem<T>(key: string): T | null {
    if (this._isSupported) {
      try {
        const item = this._storage.getItem(this.prefixedKey(key));
        return item ? JSON.parse(item) as T : null;
      } catch {
        return null;
      }
    }
    return null;
  }

  public removeItem(key: string): boolean {
    if (this._isSupported) {
      try {
        this._storage.removeItem(this.prefixedKey(key));
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }
}

export interface StorageManager<T> {
  getItem: (key: string) => T | null;
  saveItem: (key: string, value: T) => boolean;
  removeItem: (key: string) => boolean;
  unPrefixKey: (key: string | null) => string | null;
}

export default BrowserStorage;
